import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSettings } from '../contexts/SettingsContext.tsx';
import PageHeader from '../components/ui/PageHeader.tsx';
import { parseDateOnly, formatDateOnly } from '../utils/date.ts';

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfWeek = () => {
  const d = startOfToday();
  d.setDate(d.getDate() + (7 - d.getDay()));
  return d;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();

  const { data: cash, isLoading: loadingCash } = useQuery(
    ['cash-flow-dash', '2026-09-01', '2026-09-30'],
    async () => {
      const res = await api.get('/financeiro/fluxo-de-caixa', {
        params: { from: '2026-09-01', to: '2026-09-30' },
      });
      return res.data as { summary: { accumulatedBalance: number; periodIn: number; periodOut: number } };
    },
    { staleTime: 60_000 }
  );

  const { data: payables = [], isLoading: loadingPagar } = useQuery(
    'contas-a-pagar',
    async () => {
      const res = await api.get('/financeiro/contas-a-pagar');
      return res.data as Array<{
        id: string;
        description: string;
        creditor: string;
        remainingAmount: number;
        dueDate: string;
        status: string;
      }>;
    },
    { staleTime: 60_000 }
  );

  const { data: receivables = [], isLoading: loadingReceber } = useQuery(
    'contas-a-receber',
    async () => {
      const res = await api.get('/financeiro/contas-a-receber');
      return res.data as Array<{
        id: string;
        description: string;
        customer: string;
        remainingAmount: number;
        dueDate: string;
        status: string;
      }>;
    },
    { staleTime: 60_000 }
  );

  const openPayables = payables.filter(i => i.status !== 'paid');
  const openReceivables = receivables.filter(i => i.status !== 'paid');
  const saldoPagar = openPayables.reduce((s, i) => s + Number(i.remainingAmount || 0), 0);
  const saldoReceber = openReceivables.reduce((s, i) => s + Number(i.remainingAmount || 0), 0);

  const dueThisWeek = useMemo(() => {
    const from = startOfToday();
    const to = endOfWeek();
    const pagar = openPayables
      .map(i => ({
        kind: 'pagar' as const,
        id: i.id,
        label: i.description,
        party: i.creditor,
        amount: Number(i.remainingAmount || 0),
        due: parseDateOnly(i.dueDate) || new Date(0),
      }))
      .filter(i => i.due >= from && i.due <= to);
    const receber = openReceivables
      .map(i => ({
        kind: 'receber' as const,
        id: i.id,
        label: i.description,
        party: i.customer,
        amount: Number(i.remainingAmount || 0),
        due: parseDateOnly(i.dueDate) || new Date(0),
      }))
      .filter(i => i.due >= from && i.due <= to);
    return [...pagar, ...receber].sort((a, b) => a.due.getTime() - b.due.getTime()).slice(0, 6);
  }, [openPayables, openReceivables]);

  const todayLabel = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const greetingName = user?.name?.split(' ')[0] || settings?.companyName || 'Família';

  if (loadingCash || loadingPagar || loadingReceber) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Início"
        title={`Olá, ${greetingName}`}
        subtitle={`O que fazer agora · ${todayLabel}`}
      />

      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi-label">Saldo acumulado</div>
          <div className="kpi-value">
            {formatCurrency(cash?.summary?.accumulatedBalance || 0)}
          </div>
          <div className="kpi-hint">fluxo de caixa</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Saldo a pagar</div>
          <div className="kpi-value negative">{formatCurrency(saldoPagar)}</div>
          <div className="kpi-hint">{openPayables.length} contas</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Saldo a receber</div>
          <div className="kpi-value positive">{formatCurrency(saldoReceber)}</div>
          <div className="kpi-hint">{openReceivables.length} contas</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Vence esta semana</div>
          <div className="kpi-value">{dueThisWeek.length}</div>
          <div className="kpi-hint">itens acionáveis</div>
        </div>
      </div>

      <div className="action-grid">
        <div className="action-card">
          <h3>Vence esta semana</h3>
          <span className="muted">Priorize pagar / receber antes do vencimento</span>
          {dueThisWeek.length === 0 ? (
            <div className="muted" style={{ padding: '12px 0' }}>
              Nada vencendo nesta semana. Bom trabalho!
            </div>
          ) : (
            dueThisWeek.map(item => {
              const overdue = item.due < startOfToday();
              return (
                <div className="action-item" key={`${item.kind}-${item.id}`}>
                  <span className={`urgency ${overdue ? 'high' : 'med'}`} />
                  <div className="ai">
                    <strong>{item.label}</strong>
                    <span className="muted">
                      {item.party} · {item.due.toLocaleDateString('pt-BR')} ·{' '}
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                  <Link
                    className="btn btn-sm btn-secondary"
                    to={
                      item.kind === 'pagar'
                        ? `/financeiro/contas/pagar/${item.id}/pagar`
                        : `/financeiro/contas/receber/${item.id}/receber`
                    }
                    style={{ textDecoration: 'none', minHeight: 44 }}
                  >
                    {item.kind === 'pagar' ? 'Pagar' : 'Receber'}
                  </Link>
                </div>
              );
            })
          )}
        </div>

        <div className="action-card">
          <h3>Atalhos</h3>
          <span className="muted">Ações rápidas do financeiro</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
              onClick={() => navigate('/financeiro/fluxo-de-caixa/novo')}
            >
              Novo lançamento
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
              onClick={() => navigate('/financeiro/contas?tab=pagar')}
            >
              Contas a pagar
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
              onClick={() => navigate('/financeiro/contas?tab=receber')}
            >
              Contas a receber
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'center', minHeight: 44 }}
              onClick={() => navigate('/financeiro/fluxo-de-caixa')}
            >
              Ver fluxo de caixa
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
